import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { RateUnit, formatMarginWithSource, type RateUnit as RateUnitCode } from '@clawhire/shared';
import { api } from '@/lib/api';
import { EMPTY, enumOptions, formatPct, formatRate, formatRateWithHourly } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SimpleSelect } from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { PropertiesPanel } from '@/components/data/PropertiesPanel';
import { SectionCard } from '../components/SectionCard';
import { useInvalidatePositions } from '../queries';
import type { MarginResponse, PositionDetail } from '../types';

export interface RateBlockProps {
  position: PositionDetail;
  canEdit: boolean;
  /** `PATCH /positions/:id` with the given fields. */
  onPatch: (patch: Record<string, unknown>) => Promise<unknown>;
}

function parseNumber(text: string): number | null | undefined {
  const t = text.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Rates (spec §5): editable client rate + unit, effective margin with its source and the
 * position override (`POST /positions/:id/margin`), supplier ceiling, rate text on the JD, notes.
 */
export function RateBlock({ position, canEdit, onPatch }: RateBlockProps) {
  const invalidate = useInvalidatePositions();
  const { rates } = position;

  // Client rate editor
  const [rate, setRate] = React.useState(
    position.clientRateEur === null ? '' : String(position.clientRateEur),
  );
  const [unit, setUnit] = React.useState<RateUnitCode>(position.clientRateUnit);
  const [savingRate, setSavingRate] = React.useState(false);
  React.useEffect(() => {
    setRate(position.clientRateEur === null ? '' : String(position.clientRateEur));
    setUnit(position.clientRateUnit);
  }, [position.clientRateEur, position.clientRateUnit]);
  const parsedRate = parseNumber(rate);
  const rateDirty =
    parsedRate !== undefined && (parsedRate !== position.clientRateEur || unit !== position.clientRateUnit);
  const saveRate = async () => {
    if (parsedRate === undefined) return;
    setSavingRate(true);
    try {
      await onPatch({ clientRateEur: parsedRate, clientRateUnit: unit });
      toast.success('Client rate saved');
    } catch {
      /* toasted by the caller */
    } finally {
      setSavingRate(false);
    }
  };

  // Margin override
  const [override, setOverride] = React.useState(
    position.marginPct === null ? '' : String(position.marginPct),
  );
  const [reason, setReason] = React.useState('');
  React.useEffect(() => {
    setOverride(position.marginPct === null ? '' : String(position.marginPct));
  }, [position.marginPct]);
  const margin = useMutation({
    mutationFn: (body: { marginPct: number | null; reason?: string }) =>
      api.post<MarginResponse>(`/positions/${encodeURIComponent(position.id)}/margin`, body),
    onSuccess: async (res) => {
      const effective = res?.rates?.effectiveMargin;
      toast.success(effective ? `Margin → ${formatMarginWithSource(effective)}` : 'Margin updated');
      setReason('');
      await invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const parsedOverride = parseNumber(override);
  const overrideValid =
    parsedOverride !== undefined && parsedOverride !== null && parsedOverride >= 0 && parsedOverride < 100;
  const applyOverride = () => {
    if (!overrideValid || parsedOverride === position.marginPct) return;
    margin.mutate({
      marginPct: parsedOverride as number,
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    });
  };
  const useGlobal = () =>
    margin.mutate({ marginPct: null, ...(reason.trim() ? { reason: reason.trim() } : {}) });

  const effectiveText = formatMarginWithSource(rates.effectiveMargin);

  return (
    <SectionCard
      title="Rates"
      description="All calculations use EUR per hour; day rates are converted with the hours per day setting."
    >
      <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Client rate</dt>
          <dd className="mt-1 text-sm">
            {canEdit ? (
              <form
                className="flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  void saveRate();
                }}
              >
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  aria-label="Client rate (EUR)"
                  className="h-8 w-28 tabular-nums"
                  placeholder="–"
                />
                <SimpleSelect
                  value={unit}
                  options={enumOptions(RateUnit)}
                  onChange={(v) => v && setUnit(v as RateUnitCode)}
                  aria-label="Client rate unit"
                  className="h-8 w-32"
                />
                {rateDirty ? (
                  <Button type="submit" size="sm" loading={savingRate}>
                    Save
                  </Button>
                ) : null}
                {unit === 'DAY' && position.clientRateEur !== null ? (
                  <span className="text-xs text-muted-foreground">
                    = {formatRate(rates.clientRateH, 'HOUR')}
                  </span>
                ) : null}
              </form>
            ) : (
              <span className="tabular-nums">
                {formatRateWithHourly(position.clientRateEur, position.clientRateUnit, rates.hoursPerDay)}
              </span>
            )}
          </dd>
        </div>

        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Effective margin
          </dt>
          <dd className="mt-1 space-y-2 text-sm">
            <span className="text-base font-semibold tabular-nums" data-testid="effective-margin">
              {effectiveText}
            </span>
            {canEdit ? (
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  applyOverride();
                }}
              >
                <div className="space-y-1">
                  <Label htmlFor="margin-override" className="text-xs text-muted-foreground">
                    Position override (%)
                  </Label>
                  <Input
                    id="margin-override"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    max={99.99}
                    step="0.01"
                    value={override}
                    onChange={(e) => setOverride(e.target.value)}
                    placeholder={String(rates.effectiveMargin.marginPct)}
                    className="h-8 w-24 tabular-nums"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="margin-reason" className="text-xs text-muted-foreground">
                    Reason (optional)
                  </Label>
                  <Input
                    id="margin-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. strategic client"
                    className="h-8 w-44"
                    maxLength={200}
                  />
                </div>
                <Button
                  type="submit"
                  size="sm"
                  variant="secondary"
                  disabled={!overrideValid || parsedOverride === position.marginPct}
                  loading={margin.isPending && margin.variables?.marginPct !== null}
                >
                  Apply
                </Button>
                {position.marginPct !== null ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={useGlobal}
                    loading={margin.isPending && margin.variables?.marginPct === null}
                  >
                    Use global
                  </Button>
                ) : null}
              </form>
            ) : null}
          </dd>
        </div>

        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Supplier ceiling
          </dt>
          <dd className="mt-1 text-sm tabular-nums">
            {rates.supplierCeilingDisplay === null ? (
              <span className="text-muted-foreground">{EMPTY}</span>
            ) : (
              <>
                <span className="font-medium">{formatRate(rates.supplierCeilingDisplay, 'HOUR')}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  exact {rates.supplierCeiling?.toFixed(2)} · client × (1 −{' '}
                  {formatPct(rates.effectiveMargin.marginPct)})
                </span>
              </>
            )}
          </dd>
        </div>

        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Rate on the JD
          </dt>
          <dd className="mt-1 text-sm">
            <div>{rates.jdRateText}</div>
            <div className="text-xs text-muted-foreground">DE: {rates.jdRateTextDe}</div>
          </dd>
        </div>
      </dl>

      <PropertiesPanel
        columns={1}
        readOnly={!canEdit}
        onSave={onPatch}
        fields={[
          {
            key: 'clientRateNotes',
            label: 'Client rate notes',
            type: 'textarea',
            value: position.clientRateNotes,
            placeholder: 'e.g. Current lowest rate on the portal: 55 EUR/h',
          },
        ]}
      />
    </SectionCard>
  );
}
